import {
  Alert,
  Button,
  Checkbox,
  InputOtp,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
} from '@heroui/react';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { useAppSelector } from '@/redux/hooks';
import { logout } from '@/patient-app-common/slices/authSlice';
import {
  useCheckTwoFACodeMutation,
  useSendTwoFACodeMutation,
} from '@/patient-app-common/api/authApi';
import { SetTwoFA } from '@/components/settings/SetTwoFa';

export function Verify() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation('login');

  const { mfaContact, mfaMethod } = useAppSelector(({ auth }) => auth);
  const [sendTwoFACode] = useSendTwoFACodeMutation();
  const [checkTwoFaCode, { isLoading }] = useCheckTwoFACodeMutation();

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [method, setMethod] = useState(mfaMethod || 'sms');
  const [rememberBrowser, setRememberBrowser] = useState(false);

  useEffect(() => {
    if (_.isEmpty(mfaContact)) return;

    const send = async () => {
      try {
        const result = await sendTwoFACode({}).unwrap();
        if (!result.success) {
          setError(t('verify.error.send_code_failed'));
        }
      } catch (e) {
        console.error(e);
        setError(t('verify.error.send_code_failed'));
      }
    };

    send();
  }, []);

  const handleResend = async () => {
    try {
      const result = await sendTwoFACode({ method }).unwrap();

      if (!result.success) {
        setError(t('verify.error.send_code_failed'));
      }
    } catch (e) {
      console.error(e);
      setError(t('verify.error.send_code_failed'));
    }
  };

  const handleSubmit = async () => {
    if (!code || code.length < 6) {
      setError(t('verify.error.blank_input'));
    } else {
      try {
        const result = await checkTwoFaCode({
          code,
          rememberBrowser,
        }).unwrap();

        if (result.success) {
          navigate('/');
        } else {
          setError(t('verify.error.verification_failed'));
        }
      } catch (e) {
        console.error(e);
        setError(t('verify.error.verification_failed'));
      }
    }
  };

  return (
    <>
      <Modal
        backdrop="transparent"
        isOpen={true}
        hideCloseButton
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        placement="center"
        size={_.isEmpty(mfaContact) ? undefined : 'xl'}
      >
        <ModalContent>
          {_.isEmpty(mfaContact) ? (
            <SetTwoFA handleExit={() => navigate('/')} mfaType="required" />
          ) : (
            <>
              <ModalHeader>
                <h1>{t('verify.title')}</h1>
              </ModalHeader>
              <ModalBody>
                <Alert
                  color="danger"
                  description={error}
                  hideIconWrapper
                  isVisible={!_.isEmpty(error)}
                  variant="faded"
                />
                <p>{t('verify.description', { number: mfaContact })}</p>
                <InputOtp
                  color="primary"
                  className="mx-auto"
                  errorMessage={' '}
                  onChange={(e) => setCode(e.target.value)}
                  label={t('common.two_fa.input_code')}
                  length={6}
                  variant="faded"
                />
                <p>{t('common.two_fa.change_method')}</p>
                <div className="inline-flex items-center gap-8">
                  <RadioGroup
                    onChange={(e) => setMethod(e.target.value)}
                    orientation="horizontal"
                  >
                    <Radio value="sms">{t('common.two_fa.radio_sms')}</Radio>
                    <Radio value="call">{t('common.two_fa.radio_call')}</Radio>
                  </RadioGroup>
                  <ResendTimerButton
                    onResend={handleResend}
                    label={t('common.two_fa.button_resend')}
                  />
                </div>
              </ModalBody>
              <ModalFooter className="flex flex-col items-center mt-4">
                <Checkbox
                  isSelected={rememberBrowser}
                  onValueChange={setRememberBrowser}
                >
                  {t('verify.checkbox.remember_device')}
                </Checkbox>
                <Button
                  color="primary"
                  onPress={handleSubmit}
                  className="w-1/2"
                >
                  {t('verify.button.submit')}
                </Button>
                <Button
                  color="primary"
                  isLoading={isLoading}
                  onPress={() => dispatch(logout())}
                  variant="light"
                >
                  {t('common.button.return_to_login')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}

function ResendTimerButton({ onResend, label }) {
  const [timer, setTimer] = useState(30);

  useEffect(() => {
    if (timer === 0) {
      setTimer(null);
      return;
    }
    if (!timer) return;

    const intervalId = setInterval(() => {
      setTimer(timer - 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timer]);

  const btnText = useMemo(() => {
    return timer ? `${label} (${timer}s)` : label;
  }, [timer, label]);

  const handlePress = () => {
    setTimer(30);
    onResend();
  };

  return (
    <Button
      color="primary"
      isDisabled={_.isNumber(timer)}
      onPress={handlePress}
      size="sm"
      variant="flat"
    >
      {btnText}
    </Button>
  );
}
